import type { ComputedRef } from "vue";
import {
  Sizing_Pumping_Type_Enum,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";
import type { IndividualIntakeCalculations } from "../calculationTypes";
import { computed } from "vue";
import {
  getMaxFlowRateBBLD,
  getWellPressureAtDepth
} from "@/libs/calculations/ipr";
import { calculateMleEllipseMaxDepth } from "@/libs/calculations/cables";
import { turpin_coefficient } from "@/libs/calculations/pressureDropCorrelations";
import {
  extrapolateMDByTVD,
  extrapolateTVDByMD
} from "@/libs/calculations/deviationSurvey";
import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import { computeIntakeLabel as computeHpsIntakeLabel } from "@/models/equipment/hps_intake/properties";
import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import type { IntakeConditionsCtx } from "./context";

// Intake/downhole pressures and temperatures, gas separation and intake calcs.
export const defineIntakeConditions = (ctx: IntakeConditionsCtx) => {
  const {
    _scenario,
    _equipment,
    _wellProperties,
    _parameters,
    _correlations,
    isScenarioEspShore,
    totalLiquidFlowRateStbd,
    orderedIntakes,
    ascendingOrderPumps,
    tubingLengthFt,
    completePumpDepthFt,
    tubingsDepthTVDFt,
    bottomOfPumpsTVDFt,
    casingInteriorDiameterAtIntakeIn,
    intakeCrossSectionFt2,
    datumPointTVD,
    customFluidProperties,
    customViscosityProperties,
    entryRows,
    waterCutRatio,
    adjustedStaticBottomholePressurePSIA,
    basicData,
    wellTestFlowRates,
    fluidViscosityCPOverride,
    condensateApi,
    fluidPropertiesAtReservoir,
    fluidPropertiesContextAtSurface,
    stageFluidProperties,
    fluidPropertiesAtIntake,
    fluidPropertiesAfterAllGasSeparationAtPump
  } = ctx;

  const intakePressurePSIA = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtIntakePSIA;
    }
    return _wellProperties.value?.hps_pump_inlet_pressure_psia;
  });

  const pumpIntakePressurePSIA = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtPumpPSIA;
    }
    return _wellProperties.value?.hps_pump_inlet_pressure_psia;
  });

  const pumpIntakeTemperatureF = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholePumpTemperatureF;
    }
    return _wellProperties.value?.hps_pump_inlet_temperature_fahrenheit;
  });

  const calculatedIntakeGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .calculatedIntakeGasSeparationEfficiencyRatio
  );

  const overrideIntakeGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .overrideIntakeGasSeparationEfficiencyRatio
  );

  const intakeGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .intakeGasSeparationEfficiencyRatio
  );

  const naturalGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .naturalGasSeparationEfficiencyRatio
  );

  const calculatedNaturalGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .calculatedNaturalGasSeparationEfficiencyRatio
  );

  const overrideNaturalGasSeparationEfficiencyRatio = computed(
    () =>
      stageFluidProperties.value.gasSeparationEfficiencies
        .overrideNaturalGasSeparationEfficiencyRatio
  );

  const wellFlowingPressureAtPerfsPSIA = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtPerfsPSIA
  );

  const wellFlowingPressureAtSensorPSIA = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtSensorPSIA
  );

  const wellFlowingPressureAtMotorPSIA = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtMotorPSIA
  );

  const wellFlowingPressureAtSealPSIA = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputPressures
        .wellFlowingPressureAtSealPSIA
  );

  const downholeWellPerfsTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeWellPerfsTemperatureF
  );

  const downholeSensorTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeSensorTemperatureF
  );

  const downholeMotorTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeMotorTemperatureF
  );

  const downholeSealTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeSealTemperatureF
  );

  const downholeIntakeTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeIntakeTemperatureF
  );

  const downholeTubingTemperatureF = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesInputTemperatures
        .downholeTubingTemperatureF
  );

  const pumpAverageTemperatureF = computed(
    () =>
      (pumpIntakeTemperatureF.value +
        ctx.pumpData.value.dischargeTemperatureF) /
      2
  );

  // Q_max
  // This is max flow rate of the well with no equipment at bottom
  const maxFlowRateBBLD: ComputedRef<number> = computed(() => {
    if (isScenarioEspShore.value) {
      let pi = null;
      if (
        _wellProperties.value?.well_test_liquid_input_type ===
        Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
      ) {
        pi = _wellProperties.value?.well_test_productivity_index;
      }
      return getMaxFlowRateBBLD(
        _parameters.value?.flow_model as string,
        0,
        _wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
        _wellProperties.value?.well_test_static_bottom_hole_pressure_psia,
        fluidPropertiesAtReservoir.value.bubblePointPressurePSIA,
        totalLiquidFlowRateStbd.value,
        waterCutRatio.value,
        pi
      );
    } else {
      return (
        (totalLiquidFlowRateStbd.value /
          (adjustedStaticBottomholePressurePSIA.value -
            _wellProperties.value?.hps_pump_inlet_pressure_psia)) *
          adjustedStaticBottomholePressurePSIA.value || 0
      );
    }
  });

  const casingFlowRegime = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtWellPerfsContext.fluidFrictionProperties.flowRegime
  );

  // Hypothetical fluid props if pressure at intake was max pressure
  const fluidPropertiesAtMaxPressureAtIntake = computed(() => {
    // Surface gas density is hypothetical and static so we can use it to find hydrostatic
    // pressure at intake
    const intakePressureTVDFt =
      (pumpIntakePressurePSIA.value * 144.144) /
      fluidPropertiesAtIntake.value.liquidDensityLBMFT3;
    const gasHydroPressure = Math.max(
      ((tubingsDepthTVDFt.value - intakePressureTVDFt) *
        fluidPropertiesContextAtSurface.value.fluidProperties
          .gasDensityLBMFT3) /
        144.144,
      0
    );
    return getFluidPropertiesContext(
      basicData.value,
      _correlations.value,
      gasHydroPressure,
      downholeIntakeTemperatureF.value,
      casingInteriorDiameterAtIntakeIn.value,
      wellTestFlowRates.value,
      undefined,
      undefined,
      customFluidProperties.value,
      customViscosityProperties.value,
      fluidPropertiesAtReservoir.value.bubblePointPressurePSIA,
      fluidPropertiesAtReservoir.value.dewPointPressurePSIA,
      intakeCrossSectionFt2.value,
      naturalGasSeparationEfficiencyRatio.value,
      {
        fluidViscosityCPOverride: fluidViscosityCPOverride.value
      },
      condensateApi.value
    ).fluidProperties;
  });

  // Q_max_intake
  const maxFlowRateAtIntakeBBLD: ComputedRef<number> = computed(() => {
    if (isScenarioEspShore.value) {
      let pi = null;
      if (
        _wellProperties.value?.well_test_liquid_input_type ===
        Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
      ) {
        pi = _wellProperties.value?.well_test_productivity_index;
      }
      // Adjust p_wf and p_f comparing datum TVD to pump TVD
      const wellFlowingPressureAtIntakePSIA = getWellPressureAtDepth(
        datumPointTVD.value,
        // Use fluid properties at max pressure at intake since it is hypothetical
        fluidPropertiesAtMaxPressureAtIntake.value.fluidDensityLBMFT3,
        _wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
        entryRows.value,
        0,
        bottomOfPumpsTVDFt.value
      );
      const staticPressureAtIntakePSIA = getWellPressureAtDepth(
        datumPointTVD.value,
        fluidPropertiesAtMaxPressureAtIntake.value.fluidDensityLBMFT3,
        _wellProperties.value?.well_test_static_bottom_hole_pressure_psia,
        entryRows.value,
        0,
        bottomOfPumpsTVDFt.value
      );

      return getMaxFlowRateBBLD(
        _parameters.value?.flow_model as string,
        0,
        wellFlowingPressureAtIntakePSIA,
        staticPressureAtIntakePSIA,
        fluidPropertiesAtReservoir.value.bubblePointPressurePSIA,
        totalLiquidFlowRateStbd.value,
        waterCutRatio.value,
        pi
      );
    } else {
      return maxFlowRateBBLD.value;
    }
  });

  const liquidAbovePumpTvdFt = computed(() => {
    if (!isScenarioEspShore.value) {
      return 0;
    }
    const intakePressureTVDFt =
      (pumpIntakePressurePSIA.value * 144.144) /
      fluidPropertiesAtIntake.value.liquidDensityLBMFT3;

    // Gas density depends on if intake packer is installed
    const gasDensity = _parameters.value?.intake_packer_installed
      ? fluidPropertiesAtIntake.value.gasDensityLBMFT3
      : (fluidPropertiesContextAtSurface.value.fluidProperties
          .gasDensityLBMFT3 +
          fluidPropertiesAtIntake.value.gasDensityLBMFT3) /
        2;

    const gasHydroPressure =
      ((extrapolateTVDByMD(tubingLengthFt.value, entryRows.value) -
        intakePressureTVDFt) *
        gasDensity) /
      144.144;

    const casingPressurePSIA = _parameters.value?.intake_packer_installed
      ? 0
      : basicData.value.bottomCasingPressurePSIA;
    const fluidAboveIntake =
      ((pumpIntakePressurePSIA.value - casingPressurePSIA - gasHydroPressure) *
        144.144) /
      fluidPropertiesAtIntake.value.liquidDensityLBMFT3;

    return fluidAboveIntake;
  });

  const liquidAbovePumpMDFt = computed(() => {
    if (!isScenarioEspShore.value) {
      return 0;
    }
    const liquidAbovePumpTVDDepthFt =
      tubingsDepthTVDFt.value - liquidAbovePumpTvdFt.value;
    const liquidAbovePumpMDDepthFt = extrapolateMDByTVD(
      liquidAbovePumpTVDDepthFt,
      entryRows.value
    );
    // tubing length ft (md and also start of pumps md) - liquid above pump md
    // Used to use tubingLengthFt instead of completePumpDepthFt but now we could
    // have a y tool and more equipment tubing before the pump
    return completePumpDepthFt.value - liquidAbovePumpMDDepthFt;
  });

  // Gas entering pump
  const gasEnteringPumpRatio = computed(() => {
    return (
      ctx.pumpData.value.individualPumpCalculations[
        ascendingOrderPumps.value[0]?.id
      ]?.intakeStageCalculations?.intakeFluidPropertiesContext.fluidProperties
        .gasFlowVolumeRatio || 0
    );
  });

  const turpinCoefficient = computed(() => {
    return turpin_coefficient(
      fluidPropertiesAfterAllGasSeparationAtPump.value.freeGasFlowRateBBLD,
      fluidPropertiesAfterAllGasSeparationAtPump.value.liquidFlowRateBBLD,
      pumpIntakePressurePSIA.value
    );
  });

  const individualIntakeCalculations = computed(() =>
    orderedIntakes.value.reduce<IndividualIntakeCalculations>(
      (acc, xrefIntake) => {
        const shaftPowerLimitStandardHP = computed(
          () =>
            ctx.toleranceData.value.intakes[xrefIntake.id]
              .shaftPowerLimitStandardHP
        );

        const shaftPowerLimitHighStrengthHP = computed(
          () =>
            ctx.toleranceData.value.intakes[xrefIntake.id]
              .shaftPowerLimitHighStrengthHP
        );

        const title =
          _scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps
            ? computeHpsIntakeLabel(
                xrefIntake.r_intake,
                xrefIntake.use_high_strength_shaft
              )
            : computeIntakeLabel(
                xrefIntake.r_intake,
                xrefIntake.use_high_strength_shaft
              );
        const ellipseAngle = Math.atan(
          _equipment.value?.r_mle?.width_inches /
            2 /
            (xrefIntake?.r_intake?.outer_diameter_inches +
              _equipment.value?.r_mle?.depth_inches)
        );
        const mleMaxEllipseDepthIn = calculateMleEllipseMaxDepth(
          ellipseAngle,
          _equipment.value?.r_mle?.depth_inches,
          xrefIntake?.r_intake?.outer_diameter_inches,
          0,
          false
        );
        const maxCalculatedOuterDiameterIn =
          mleMaxEllipseDepthIn + xrefIntake?.r_intake?.outer_diameter_inches;
        acc[xrefIntake.id] = {
          shaftPowerLimitStandardHP: shaftPowerLimitStandardHP.value,
          shaftPowerLimitHighStrengthHP: shaftPowerLimitHighStrengthHP.value,
          standardShaftRatingRatio:
            shaftPowerLimitStandardHP.value === 0
              ? 0
              : ctx.totalRequiredBrakeHP.value /
                shaftPowerLimitStandardHP.value,
          highStrengthShaftRatingRatio:
            shaftPowerLimitHighStrengthHP.value === 0
              ? 0
              : ctx.totalRequiredBrakeHP.value /
                shaftPowerLimitHighStrengthHP.value,
          title,
          mleMaxEllipseDepthIn,
          maxCalculatedOuterDiameterIn,
          xrefIntake
        };

        return acc;
      },
      {}
    )
  );

  return {
    intakePressurePSIA,
    pumpIntakePressurePSIA,
    pumpIntakeTemperatureF,
    calculatedIntakeGasSeparationEfficiencyRatio,
    overrideIntakeGasSeparationEfficiencyRatio,
    intakeGasSeparationEfficiencyRatio,
    naturalGasSeparationEfficiencyRatio,
    calculatedNaturalGasSeparationEfficiencyRatio,
    overrideNaturalGasSeparationEfficiencyRatio,
    wellFlowingPressureAtPerfsPSIA,
    wellFlowingPressureAtSensorPSIA,
    wellFlowingPressureAtMotorPSIA,
    wellFlowingPressureAtSealPSIA,
    downholeWellPerfsTemperatureF,
    downholeSensorTemperatureF,
    downholeMotorTemperatureF,
    downholeSealTemperatureF,
    downholeIntakeTemperatureF,
    downholeTubingTemperatureF,
    pumpAverageTemperatureF,
    maxFlowRateBBLD,
    casingFlowRegime,
    fluidPropertiesAtMaxPressureAtIntake,
    maxFlowRateAtIntakeBBLD,
    liquidAbovePumpTvdFt,
    liquidAbovePumpMDFt,
    gasEnteringPumpRatio,
    turpinCoefficient,
    individualIntakeCalculations
  };
};

export type IntakeConditionsCalcs = ReturnType<typeof defineIntakeConditions>;
